import { Button } from "@/components/ui/button";
import { Phone, MessageCircle, Star, Users, Clock } from "lucide-react";

const HeroSection = () => {
  const stats = [
    { icon: Users, value: "15,000+", label: "Consultations" },
    { icon: Clock, value: "15+", label: "Years Experience" },
    { icon: Star, value: "4.9", label: "Rating" },
  ];

  return (
    <section
      id="home"
      className="relative min-h-screen flex items-center overflow-hidden"
    >
      {/* Background - Warm brown to golden-brown gradient */}
      <div className="absolute inset-0 bg-gradient-hero" />
      
      {/* Decorative Elements */}
      <div className="absolute inset-0 overflow-hidden">
        {/* Zodiac wheel - subtle background */}
        <div className="absolute -right-40 top-1/2 -translate-y-1/2 w-[800px] h-[800px] opacity-5">
          <svg viewBox="0 0 100 100" className="w-full h-full animate-spin-slow">
            <circle cx="50" cy="50" r="45" fill="none" stroke="currentColor" strokeWidth="0.5" className="text-gold" />
            <circle cx="50" cy="50" r="35" fill="none" stroke="currentColor" strokeWidth="0.3" className="text-gold" />
            <circle cx="50" cy="50" r="25" fill="none" stroke="currentColor" strokeWidth="0.3" className="text-amber" />
          </svg>
        </div>
        
        {/* Stars */}
        {[5, 15, 25, 35, 45, 55, 65, 75, 85, 95, 10, 30, 50, 70, 90, 20, 40, 60, 80, 8].map((pos, i) => (
          <div
            key={i}
            className="absolute w-1 h-1 bg-gold/30 rounded-full animate-twinkle"
            style={{
              left: `${pos}%`,
              top: `${(i * 5 + 3) % 100}%`,
              animationDelay: `${(i * 0.15) % 3}s`,
            }}
          />
        ))}
      </div>

      {/* Noise Overlay */}
      <div className="absolute inset-0 noise-overlay" />

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Content */}
          <div className="text-center lg:text-left">
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-foreground leading-tight mb-4">
              <span className="text-gradient-gold">Tapask</span>
              <br />
              <span className="text-foreground">Astrologer</span>
            </h1>
            
            <h2 className="font-display text-xl sm:text-2xl text-primary mb-6">
              Pandit Tapas Dutta
            </h2>
            
            <p className="text-lg text-muted-foreground max-w-xl mx-auto lg:mx-0 mb-8 leading-relaxed">
              Your Path to Spiritual Enlightenment & Divine Solutions. 
              Discover the ancient wisdom of Vedic astrology and transform your life.
            </p>

            {/* Stats */}
            <div className="flex flex-wrap justify-center lg:justify-start gap-6 mb-10">
              {stats.map((stat, index) => (
                <div
                  key={index}
                  className="flex items-center gap-3 bg-secondary/50 backdrop-blur-sm rounded-lg px-4 py-3 border border-border/50"
                >
                  <stat.icon className="w-5 h-5 text-primary" />
                  <div>
                    <p className="font-display text-lg font-semibold text-foreground">
                      {stat.value}
                    </p>
                    <p className="text-xs text-muted-foreground">{stat.label}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
              <a
                href="https://wa.me/919870511379?text=Namaste%20Pandit%20ji%2C%20I%20would%20like%20to%20book%20a%20consultation."
                target="_blank"
                rel="noopener noreferrer"
              >
                <Button
                  size="lg"
                  className="bg-primary text-primary-foreground hover:bg-primary/90 gap-2 text-base px-8 py-6 rounded-full shadow-gold pulse-glow w-full sm:w-auto"
                >
                  <MessageCircle className="w-5 h-5" />
                  Book Consultation
                </Button>
              </a>
              <a href="tel:+919870511379">
                <Button
                  size="lg"
                  variant="outline"
                  className="border-primary text-primary hover:bg-primary/10 gap-2 text-base px-8 py-6 rounded-full w-full sm:w-auto bg-background/50"
                >
                  <Phone className="w-5 h-5" />
                  Call Now
                </Button>
              </a>
            </div>
          </div>

          {/* Image */}
          <div className="relative flex justify-center lg:justify-end">
            <div className="relative">
              {/* Glow Effect */}
              <div className="absolute -inset-4 bg-gradient-to-br from-gold/20 to-amber/10 rounded-3xl blur-2xl" />
              
              {/* Image Container */}
              <div className="relative bg-gradient-to-br from-secondary to-card rounded-3xl p-2 border border-border/50 overflow-hidden">
                <div className="relative w-72 h-96 sm:w-80 sm:h-[440px] lg:w-96 lg:h-[500px] rounded-2xl overflow-hidden bg-white">
                  <img
                    src="/images/tapask.jpg"
                    alt="Pandit Tapas Dutta - Tapask Astrologer"
                    className="w-full h-full object-cover"
                  />
                  {/* Overlay Gradient */}
                  <div className="absolute inset-0 bg-gradient-to-t from-background/60 via-transparent to-transparent" />
                </div>
              </div>

              {/* Floating Badge */}
              <div className="absolute -bottom-4 -left-4 bg-card border border-border rounded-xl p-4 shadow-lg animate-float">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
                    <Star className="w-5 h-5 text-primary fill-primary" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground">Expert in</p>
                    <p className="text-xs text-muted-foreground">Vedic Astrology</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Gradient */}
      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-background to-transparent" />
    </section>
  );
};

export default HeroSection;
